import { UseQueryOptions } from "react-query";

export type Module =
  | "account_companies"
  | "account_users"
  | "additional_i18ns"
  | "baskets"
  | "campaigns"
  | "campaign_opportunities"
  | "commissions"
  | "company"
  | "discount_codes"
  | "discounts"
  | "document_invoices"
  | "document_offer"
  | "document_offers"
  | "document_offer_question"
  | "document_offer_questions"
  | "document_orders"
  | "email_accounts"
  | "favorites"
  | "holidays"
  | "places"
  | "price_individuals"
  | "products"
  | "serviced_products"
  | "tasks"
  | "user_user_absence_limits"
  | "user_user_absences"
  | "user_users"
  | "message_threads"
  | "user_user_qualifications";

export enum ModuleCredential {
  None,
  Show,
  Edit,
  Create,
  Delete,
}

export enum FieldCredential {
  None,
  Show,
  Edit,
}

export type FieldType =
  | "integer"
  | "string"
  | "datetime"
  | "date"
  | "text"
  | "relation"
  | "boolean"
  | "bigint"
  | "float";

type RelationCredentialTypes =
  | "documentInvoices"
  | "documentStores"
  | "documentOrders"
  | "tasks"
  | "documentOfferQuestions"
  | "campaignOpportunities"
  | "emailMessages"
  | "commissions"
  | "communicationThreads"
  | "odbcReports"
  | "products"
  | "servicedProducts";

export interface AttributeCredential {
  id: number;
  uuid: string | null;
  name: string;
  type: string;
  credential: FieldCredential;
  options: {
    displayPriority?: number;
    ordering?: number;
    forPosition: boolean;
    registerEventOnchange: boolean;
    tooltip: null | string;
    asHTML?: boolean;
  };
  positions?: AttributeCredentialPosition[];
}

export type AttributeCredentialPosition = {
  id: number;
  value: string;
  ordering: number | null;
};
export interface CommissionMetaParams {
  "for[id]"?: number;
  "for[commissionPhase]"?: number;
  "for[documentSeriesDefinition]"?: number;
  "setting[with_editable_settings_for_action]"?: boolean;
}

export interface TaskMetaParams {
  "for[id]"?: number;
  "for[type]"?: number;
  extra_fields?: string;
}

export interface ProductMetaParams {
  "for[id]"?: number;
  "for[category]"?: number;
}

export interface UserMetaParams {
  "for[id]"?: number;
  extra_fields?: string;
  "setting[with_deleted]"?: boolean;
}

export type ModuleParams =
  | CommissionMetaParams
  | TaskMetaParams
  | ProductMetaParams
  | UserMetaParams;
export interface ExtraFieldRelationsCredentials {
  type: "object";
  credential: FieldCredential;
  properties: {
    [key in RelationCredentialTypes]?: {
      credential: FieldCredential;
    };
  };
}

export interface ModuleMetaField {
  credential: FieldCredential;
  type: FieldType;
  relatedModule: null | string;
  isUnique: boolean;
  isNullable: boolean;
  isHtml?: boolean;
}
export interface ModuleMeta {
  for: {
    commissionPhase?: number;
    documentSeriesDefinition?: number;
  };
  module: {
    credential: ModuleCredential;
  };
  fields: {
    [key: string]: ModuleMetaField;
  };
  extra_fields: {
    attributes?: {
      type: "array";
      credential: FieldCredential;
      items: AttributeCredential[];
    };
    relations?: ExtraFieldRelationsCredentials;
  };
  settings: [];
  otherModulesCredential?: {
    basket?: number;
    emailMessage?: number;
  };
  actions?: { [key: string]: 0 | 1 };
  subtypes?: { [key: string]: { credential: FieldCredential } };
}

export interface CredentialContextProviderProps {
  module: Module;
  initialCredentials?: Partial<ModuleMeta>;
  hasCredentialsFn?: HasCredential;
  hasModuleCredentialFn?: (
    credentialType: CredentialType,
    customCredentialValue?: number
  ) => boolean;
  hasActionCredentialFn?: (action: string) => boolean;
  additionalKeys?: any;
  credentialFetcherFn?: (module: Module) => Promise<ModuleMeta>;
  initialModuleParams?: ModuleParams;
  useQueryOptions?: UseQueryOptions;
}

export type CredentialType = "show" | "edit" | "create" | "delete";
export type CredentialObjectType =
  | "relation"
  | "attribute"
  | "field"
  | "extraField"
  | "module";
export interface CredentialContextType {
  meta: ModuleMeta | null;
  setModuleParams: React.Dispatch<React.SetStateAction<ModuleParams | null>>;
  hasCredential: HasCredential;
  hasModuleCredential: (
    type: CredentialType,
    customCredentialValue?: number
  ) => boolean;
  hasActionCredential: (action: string) => boolean;
  isLoading: boolean;
  isFetching: boolean;
}

export type PropsAreEqual<P> = (
  prevProps: Readonly<P>,
  nextProps: Readonly<P>
) => boolean;

export type CredentialConfig = {
  name: string;
  objectType: CredentialObjectType;
  credentialType: CredentialType;
};

export type HasCredential = (
  credentialIdentifier: string | number,
  objectType: CredentialObjectType,
  type?: CredentialType
) => boolean;
